import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiOrNull, MARKETPLACE, marketplaceKeys } from './client';
import type {
  MyBooking,
  Preferences,
  RebookSuggestion,
  RescheduleRequest,
  Review,
  SubmitReviewRequest,
} from './contracts';

// ─── §6 Cancel / reschedule / rebook ───────────────────────────────────

/** Replaces one booking (by id) inside every cached `myBookings` list. */
function patchCachedBooking(qc: ReturnType<typeof useQueryClient>, updated: MyBooking) {
  qc.setQueriesData<MyBooking[]>({ queryKey: marketplaceKeys.myBookingsAll }, (list) =>
    Array.isArray(list) ? list.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)) : list,
  );
}

/**
 * `POST /me/bookings/{id}/cancel` → updated `MyBooking`. Rejects with an
 * `ApiError` (`cancellation_window_passed`, 404 …) — render via `toUserMessage`.
 */
export function useMyBookingCancel() {
  const qc = useQueryClient();
  return useMutation<MyBooking, Error, string>({
    mutationFn: (bookingId) =>
      api<MyBooking>(`${MARKETPLACE}/me/bookings/${encodeURIComponent(bookingId)}/cancel`, {
        method: 'POST',
      }),
    onSuccess: (updated) => {
      if (updated && updated.id) patchCachedBooking(qc, updated);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: marketplaceKeys.myBookingsAll });
      // A cancelled slot frees availability everywhere.
      qc.invalidateQueries({ queryKey: marketplaceKeys.availabilityAll });
      qc.invalidateQueries({ queryKey: marketplaceKeys.availabilityRangeAll });
    },
  });
}

/**
 * `POST /me/bookings/{id}/reschedule` → updated `MyBooking`. Rejects with
 * `slot_taken` / `reschedule_window_passed`.
 */
export function useMyBookingReschedule() {
  const qc = useQueryClient();
  return useMutation<MyBooking, Error, { bookingId: string } & RescheduleRequest>({
    mutationFn: ({ bookingId, ...req }) =>
      api<MyBooking>(`${MARKETPLACE}/me/bookings/${encodeURIComponent(bookingId)}/reschedule`, {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: (updated) => {
      if (updated && updated.id) patchCachedBooking(qc, updated);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: marketplaceKeys.myBookingsAll });
      qc.invalidateQueries({ queryKey: marketplaceKeys.availabilityAll });
      qc.invalidateQueries({ queryKey: marketplaceKeys.availabilityRangeAll });
      qc.invalidateQueries({ queryKey: marketplaceKeys.rebookSuggestion });
    },
  });
}

/**
 * `GET /me/rebook-suggestion`. `null` when there's nothing to suggest (204),
 * the user is signed out (401) or the backend doesn't have it yet (404).
 */
export function useRebookSuggestion(opts: { enabled?: boolean } = {}) {
  return useQuery<RebookSuggestion | null>({
    queryKey: marketplaceKeys.rebookSuggestion,
    enabled: opts.enabled ?? true,
    queryFn: () =>
      apiOrNull<RebookSuggestion>(`${MARKETPLACE}/me/rebook-suggestion`, {}, {
        nullOnUnauthorized: true,
      }),
    staleTime: 5 * 60_000,
  });
}

// ─── §7 Preferences ────────────────────────────────────────────────────

/** `GET /me/preferences`. `null` when signed out or not on the backend yet. */
export function usePreferences(opts: { enabled?: boolean } = {}) {
  return useQuery<Preferences | null>({
    queryKey: marketplaceKeys.preferences,
    enabled: opts.enabled ?? true,
    queryFn: () =>
      apiOrNull<Preferences>(`${MARKETPLACE}/me/preferences`, {}, { nullOnUnauthorized: true }),
    staleTime: 60_000,
  });
}

export const PREFERENCES_MUTATION_KEY = ['marketplace-preferences-update'] as const;

/**
 * `PUT /me/preferences` with only the changed field(s) — the PUT is partial
 * (as built, M5) — and an optimistic update (M5 QA 2).
 *
 * Rapid toggles used to race: every PUT carried the whole object and the last
 * response to land won, even if it was the older one. Now:
 * - all preference saves share one mutation scope, so they run one after the
 *   other in click order;
 * - each optimistic update only touches its own field(s);
 * - a response is written to the cache only when no later save is queued
 *   (the newest intent stays on screen);
 * - on error the failed field(s) roll back to their value before this save,
 *   unless a later save of the same preferences is queued (it decides).
 */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation<Preferences | null, Error, Partial<Preferences>, { before: Partial<Preferences> }>({
    mutationKey: PREFERENCES_MUTATION_KEY,
    scope: { id: 'preferences' },
    mutationFn: (patch) =>
      api<Preferences>(`${MARKETPLACE}/me/preferences`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: marketplaceKeys.preferences });
      const old = qc.getQueryData<Preferences | null>(marketplaceKeys.preferences);
      const before: Partial<Preferences> = {};
      for (const k of Object.keys(patch) as (keyof Preferences)[]) if (old) before[k] = old[k];
      qc.setQueryData<Preferences | null>(marketplaceKeys.preferences, (cur) => (cur ? { ...cur, ...patch } : cur));
      return { before };
    },
    onError: (_err, _patch, ctx) => {
      if (qc.isMutating({ mutationKey: PREFERENCES_MUTATION_KEY }) > 1 || !ctx) return;
      qc.setQueryData<Preferences | null>(marketplaceKeys.preferences, (cur) => (cur ? { ...cur, ...ctx.before } : cur));
    },
    onSuccess: (saved) => {
      // Another save is still queued: its optimistic value is the user's intent.
      if (qc.isMutating({ mutationKey: PREFERENCES_MUTATION_KEY }) > 1) return;
      if (saved && typeof saved === 'object') qc.setQueryData(marketplaceKeys.preferences, saved);
    },
  });
}

// ─── §8 Submit a review ────────────────────────────────────────────────

/**
 * `POST /me/bookings/{id}/review` → `201 Review`. Rejects (as built, M6) with
 * 404 `booking_not_found`, 409 `already_reviewed`, 403 `not_eligible`
 * (`details.reason`), 400 `invalid_request` (`details.field`), 429.
 *
 * On success the row flips to "You rated it N stars" straight away (cache
 * patch), then `myBookings` and the salon's reviews/profile are refetched.
 * A 409/403 also refetches `myBookings`: `canReview` was stale.
 */
export function useSubmitReview() {
  const qc = useQueryClient();
  return useMutation<Review, Error, { bookingId: string; subdomain?: string } & SubmitReviewRequest>({
    mutationFn: ({ bookingId, rating, text }) => {
      const trimmed = text?.trim();
      return api<Review>(`${MARKETPLACE}/me/bookings/${encodeURIComponent(bookingId)}/review`, {
        method: 'POST',
        body: JSON.stringify(trimmed ? { rating, text: trimmed } : { rating }),
      });
    },
    onSuccess: (review, vars) => {
      qc.setQueriesData<MyBooking[]>({ queryKey: marketplaceKeys.myBookingsAll }, (list) =>
        Array.isArray(list)
          ? list.map((b) =>
              b.id === vars.bookingId ? { ...b, review: { rating: review?.rating ?? vars.rating }, canReview: false } : b,
            )
          : list,
      );
      qc.invalidateQueries({ queryKey: marketplaceKeys.myBookingsAll });
      if (vars.subdomain) {
        qc.invalidateQueries({ queryKey: marketplaceKeys.salonReviewsAll(vars.subdomain) });
        qc.invalidateQueries({ queryKey: marketplaceKeys.salon(vars.subdomain) });
      }
    },
    onError: (err) => {
      const status = (err as { status?: number }).status;
      if (status === 403 || status === 409 || status === 404) {
        qc.invalidateQueries({ queryKey: marketplaceKeys.myBookingsAll });
      }
    },
  });
}
